import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './RifleScore.css';
import { ArrowLeft, Check, Crosshair, Target, Users } from 'lucide-react';
import { rifleService } from '../../../../../services';

const createFallbackScores = (shotCount) =>
    Array.from({ length: shotCount }, (_, index) => ({
        shotNumber: index + 1,
        value: '',
        vBull: false
    }));

const RifleScoreSheetDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [scoreSheet, setScoreSheet] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRoundId, setSelectedRoundId] = useState(null);
    const [editableScores, setEditableScores] = useState([]);
    const [isSavingRound, setIsSavingRound] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);
    const [overviewForm, setOverviewForm] = useState(null);
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [newRoundForm, setNewRoundForm] = useState({ shooterName: '', notes: '' });
    const [isAddingRound, setIsAddingRound] = useState(false);
    const canManage = scoreSheet?.canManage !== false;

    useEffect(() => {
        const fetchScoreSheet = async () => {
            try {
                const data = await rifleService.getScoreSheet(id);
                setScoreSheet(data);
            } catch (err) {
                console.error('Error loading rifle score sheet:', err);
                setError('Unable to load this rifle score sheet.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchScoreSheet();
    }, [id]);

    useEffect(() => {
        if (!scoreSheet) {
            setOverviewForm(null);
            return;
        }

        setOverviewForm({
            disciplineType: scoreSheet.disciplineType || 'TargetRifle',
            variant: scoreSheet.variant || '',
            scoreSheetType: scoreSheet.scoreSheetType || 'Practice',
            distance: scoreSheet.distance ?? '',
            distanceUnit: scoreSheet.distanceUnit || 'metres',
            numberOfShots: scoreSheet.numberOfShots ?? '',
            position: scoreSheet.position || 'Prone',
            weatherConditions: scoreSheet.weatherConditions || '',
            rangeName: scoreSheet.rangeName || '',
            notes: scoreSheet.notes || ''
        });
        setNewRoundForm((current) => ({
            ...current,
            shooterName: current.shooterName || scoreSheet.shooters?.[0]?.shooterName || ''
        }));
    }, [scoreSheet]);

    const selectedRound = useMemo(
        () => scoreSheet?.rounds?.find((round) => round.roundId === selectedRoundId) || scoreSheet?.rounds?.[0] || null,
        [scoreSheet, selectedRoundId]
    );

    useEffect(() => {
        if (!scoreSheet?.rounds?.length) {
            setSelectedRoundId(null);
            setEditableScores([]);
            return;
        }

        const round = scoreSheet.rounds.find((item) => item.roundId === selectedRoundId) || scoreSheet.rounds[0];
        setSelectedRoundId(round.roundId);

        try {
            const parsed = round.scoreData ? JSON.parse(round.scoreData) : null;
            const shotCount = parsed?.numberOfShots || scoreSheet.numberOfShots || parsed?.scores?.length || 10;
            const scores = (parsed?.scores?.length ? parsed.scores : createFallbackScores(shotCount)).map((score, index) => ({
                shotNumber: score.shotNumber || index + 1,
                value: score.value ?? '',
                vBull: Boolean(score.vBull)
            }));
            setEditableScores(scores);
        } catch (parseError) {
            console.error('Error parsing rifle score data:', parseError);
            setEditableScores(createFallbackScores(scoreSheet.numberOfShots || 10));
        }
    }, [scoreSheet, selectedRoundId]);

    const computedTotals = useMemo(() => {
        const totalScore = editableScores.reduce((total, score) => total + (Number(score.value) || 0), 0);
        const vBullCount = editableScores.filter((score) => score.vBull).length;
        return { totalScore, vBullCount };
    }, [editableScores]);

    const handleScoreChange = (index, nextValue) => {
        if (!canManage) {
            return;
        }

        const numericValue = nextValue === '' ? '' : Math.max(0, Math.min(10, Number(nextValue)));
        setEditableScores((current) =>
            current.map((score, currentIndex) =>
                currentIndex === index ? { ...score, value: numericValue } : score
            )
        );
        setSaveMessage(null);
    };

    const handleVBullToggle = (index) => {
        if (!canManage) {
            return;
        }

        setEditableScores((current) =>
            current.map((score, currentIndex) =>
                currentIndex === index ? { ...score, vBull: !score.vBull } : score
            )
        );
        setSaveMessage(null);
    };

    const handleSaveRound = async () => {
        if (!canManage || !selectedRound || !scoreSheet) {
            return;
        }

        const scoreData = JSON.stringify({
            disciplineType: scoreSheet.disciplineType,
            numberOfShots: editableScores.length,
            scores: editableScores.map((score) => ({
                shotNumber: score.shotNumber,
                value: score.value === '' ? null : Number(score.value),
                vBull: score.vBull
            })),
            totalScore: computedTotals.totalScore,
            vBullCount: computedTotals.vBullCount
        });

        setIsSavingRound(true);
        setSaveMessage(null);

        try {
            await rifleService.updateRound(scoreSheet.rifleScoreSheetId, selectedRound.roundId, {
                scoreData,
                totalScore: computedTotals.totalScore,
                vBullCount: computedTotals.vBullCount
            });

            setScoreSheet((current) => {
                const updatedRounds = current.rounds.map((round) =>
                    round.roundId === selectedRound.roundId
                        ? {
                            ...round,
                            scoreData,
                            totalScore: computedTotals.totalScore,
                            vBullCount: computedTotals.vBullCount
                        }
                        : round
                );
                const recalculatedBest = updatedRounds.reduce(
                    (best, round) => Math.max(best, round.totalScore ?? 0),
                    0
                );

                return {
                    ...current,
                    totalScore: recalculatedBest,
                    bestRoundScore: recalculatedBest,
                    rounds: updatedRounds
                };
            });
            setSaveMessage('Round saved');
        } catch (saveError) {
            console.error('Error saving rifle round:', saveError);
            setSaveMessage('Save failed');
        } finally {
            setIsSavingRound(false);
        }
    };

    const handleSaveDetails = async () => {
        if (!canManage || !overviewForm || !scoreSheet) {
            return;
        }

        setIsSavingDetails(true);
        setSaveMessage(null);

        try {
            await rifleService.updateScoreSheet(scoreSheet.rifleScoreSheetId, {
                disciplineType: overviewForm.disciplineType,
                variant: overviewForm.variant || null,
                scoreSheetType: overviewForm.scoreSheetType,
                distance: overviewForm.distance === '' ? null : Number(overviewForm.distance),
                distanceUnit: overviewForm.distanceUnit || null,
                numberOfShots: overviewForm.numberOfShots === '' ? null : Number(overviewForm.numberOfShots),
                position: overviewForm.position || null,
                weatherConditions: overviewForm.weatherConditions || null,
                rangeName: overviewForm.rangeName || null,
                notes: overviewForm.notes || null
            });

            setScoreSheet((current) => ({
                ...current,
                disciplineType: overviewForm.disciplineType,
                variant: overviewForm.variant,
                scoreSheetType: overviewForm.scoreSheetType,
                distance: overviewForm.distance === '' ? null : Number(overviewForm.distance),
                distanceUnit: overviewForm.distanceUnit,
                numberOfShots: overviewForm.numberOfShots === '' ? null : Number(overviewForm.numberOfShots),
                position: overviewForm.position,
                weatherConditions: overviewForm.weatherConditions,
                rangeName: overviewForm.rangeName,
                notes: overviewForm.notes,
                possibleScore: overviewForm.numberOfShots === '' ? null : Number(overviewForm.numberOfShots) * 10
            }));
            setSaveMessage('Sheet details saved');
        } catch (detailsError) {
            console.error('Error saving rifle sheet details:', detailsError);
            setSaveMessage('Could not save sheet details');
        } finally {
            setIsSavingDetails(false);
        }
    };

    const handleAddRound = async () => {
        if (!canManage || !scoreSheet) {
            return;
        }

        setIsAddingRound(true);
        setSaveMessage(null);

        try {
            const createdRound = await rifleService.addRound(scoreSheet.rifleScoreSheetId, {
                roundNumber: (scoreSheet.rounds?.length || 0) + 1,
                shooterName: newRoundForm.shooterName || null,
                notes: newRoundForm.notes || null
            });
            setScoreSheet((current) => ({
                ...current,
                rounds: [...(current.rounds || []), createdRound]
            }));
            setSelectedRoundId(createdRound.roundId);
            setNewRoundForm({
                shooterName: newRoundForm.shooterName,
                notes: ''
            });
            setSaveMessage('Round added');
        } catch (roundError) {
            console.error('Error adding rifle round:', roundError);
            setSaveMessage('Could not add round');
        } finally {
            setIsAddingRound(false);
        }
    };

    if (isLoading) {
        return (
            <div className="rifle-loading">
                <div className="rifle-spinner"></div>
                <p>Loading rifle score sheet...</p>
            </div>
        );
    }

    if (error || !scoreSheet) {
        return <div className="rifle-error-message">{error || 'Score sheet not found.'}</div>;
    }

    return (
        <div className="rifle-score-page">
            <div className="rifle-detail-actions">
                <button className="rifle-secondary-btn" onClick={() => navigate('/dashboard/score/rifle')}>
                    <ArrowLeft size={16} />
                    Back to rifle dashboard
                </button>
            </div>

            <div className="rifle-header">
                <div className="rifle-header-content">
                    <div className="rifle-header-icon">
                        <Crosshair size={28} />
                    </div>
                    <div className="rifle-header-text">
                        <h1>{scoreSheet.eventName || 'Rifle score sheet'}</h1>
                        <p>{scoreSheet.disciplineType || 'Rifle'}{scoreSheet.variant ? ` - ${scoreSheet.variant}` : ''}</p>
                    </div>
                </div>
            </div>

            <div className="rifle-detail-grid">
                <section className="rifle-detail-card">
                    <h3>Overview</h3>
                    {!canManage ? (
                        <div className="instructions-card">
                            <p>You can view this rifle score sheet because you were added as a shooter.</p>
                            <p>Only the event owner or Shoot Connect staff can edit details, add rounds, or record scores.</p>
                        </div>
                    ) : null}
                    <div className="rifle-detail-stats">
                        <div className="rifle-detail-stat">
                            <Target size={18} />
                            <div>
                                <span>Best score</span>
                                <strong>{scoreSheet.totalScore ?? 0}/{scoreSheet.possibleScore ?? 0}</strong>
                            </div>
                        </div>
                        <div className="rifle-detail-stat">
                            <Users size={18} />
                            <div>
                                <span>Shooters</span>
                                <strong>{scoreSheet.shooterCount || scoreSheet.shooters?.length || 0}</strong>
                            </div>
                        </div>
                    </div>
                    {overviewForm ? (
                        <>
                            <div className="rifle-form-grid">
                                <div className="rifle-form-group">
                                    <label>Discipline</label>
                                    <input
                                        type="text"
                                        value={overviewForm.disciplineType}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, disciplineType: event.target.value }))}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="rifle-form-group">
                                    <label>Variant</label>
                                    <input
                                        type="text"
                                        value={overviewForm.variant}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, variant: event.target.value }))}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="rifle-form-group">
                                    <label>Sheet type</label>
                                    <input
                                        type="text"
                                        value={overviewForm.scoreSheetType}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, scoreSheetType: event.target.value }))}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="rifle-form-group">
                                    <label>Position</label>
                                    <input
                                        type="text"
                                        value={overviewForm.position}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, position: event.target.value }))}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="rifle-form-group">
                                    <label>Distance</label>
                                    <input
                                        type="number"
                                        value={overviewForm.distance}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, distance: event.target.value }))}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="rifle-form-group">
                                    <label>Unit</label>
                                    <select
                                        value={overviewForm.distanceUnit}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, distanceUnit: event.target.value }))}
                                        disabled={!canManage}
                                    >
                                        <option value="metres">metres</option>
                                        <option value="yards">yards</option>
                                    </select>
                                </div>
                                <div className="rifle-form-group">
                                    <label>Shots</label>
                                    <input
                                        type="number"
                                        value={overviewForm.numberOfShots}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, numberOfShots: event.target.value }))}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="rifle-form-group">
                                    <label>Range</label>
                                    <input
                                        type="text"
                                        value={overviewForm.rangeName}
                                        onChange={(event) => setOverviewForm((current) => ({ ...current, rangeName: event.target.value }))}
                                        disabled={!canManage}
                                    />
                                </div>
                            </div>
                            <div className="rifle-form-group">
                                <label>Weather</label>
                                <input
                                    type="text"
                                    value={overviewForm.weatherConditions}
                                    onChange={(event) => setOverviewForm((current) => ({ ...current, weatherConditions: event.target.value }))}
                                    disabled={!canManage}
                                />
                            </div>
                            <div className="rifle-form-group">
                                <label>Notes</label>
                                <textarea
                                    value={overviewForm.notes}
                                    onChange={(event) => setOverviewForm((current) => ({ ...current, notes: event.target.value }))}
                                    disabled={!canManage}
                                />
                            </div>
                            {canManage ? (
                                <div className="rifle-form-actions">
                                    <button
                                        type="button"
                                        className="rifle-view-btn"
                                        onClick={handleSaveDetails}
                                        disabled={isSavingDetails}
                                    >
                                        <Check size={16} />
                                        {isSavingDetails ? 'Saving...' : 'Save details'}
                                    </button>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </section>

                <section className="rifle-detail-card">
                    <h3>Shooters</h3>
                    {scoreSheet.shooters?.length ? (
                        <div className="rifle-detail-stack">
                            {scoreSheet.shooters.map((shooter) => (
                                <div key={shooter.rifleScoreSheetShooterId} className="rifle-detail-row">
                                    <span>{shooter.shooterName || 'Unnamed shooter'}</span>
                                    <strong>Position {shooter.position}</strong>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="rifle-detail-empty">No shooters assigned yet.</p>
                    )}
                </section>
            </div>

            <section className="rifle-detail-card">
                <h3>Rounds</h3>
                {canManage ? (
                    <div className="rifle-inline-form">
                        <div className="rifle-form-group">
                            <label>Shooter name</label>
                            <input
                                type="text"
                                value={newRoundForm.shooterName}
                                onChange={(event) => setNewRoundForm((current) => ({ ...current, shooterName: event.target.value }))}
                                placeholder="Shooter for next round"
                            />
                        </div>
                        <div className="rifle-form-group">
                            <label>Round notes</label>
                            <input
                                type="text"
                                value={newRoundForm.notes}
                                onChange={(event) => setNewRoundForm((current) => ({ ...current, notes: event.target.value }))}
                                placeholder="Optional notes"
                            />
                        </div>
                        <div className="rifle-form-actions rifle-form-actions-end">
                            <button
                                type="button"
                                className="rifle-view-btn"
                                onClick={handleAddRound}
                                disabled={isAddingRound}
                            >
                                <Check size={16} />
                                {isAddingRound ? 'Adding...' : 'Add round'}
                            </button>
                        </div>
                    </div>
                ) : null}
                {scoreSheet.rounds?.length ? (
                    <>
                        <div className="rifle-round-tabs">
                            {scoreSheet.rounds.map((round) => (
                                <button
                                    key={round.roundId}
                                    className={`rifle-round-tab ${selectedRound?.roundId === round.roundId ? 'active' : ''}`}
                                    onClick={() => setSelectedRoundId(round.roundId)}
                                >
                                    Round {round.roundNumber}
                                    <span>{round.shooterName || 'Shooter'}</span>
                                </button>
                            ))}
                        </div>

                        {selectedRound && (
                            <div className="rifle-target-card">
                                <div className="rifle-target-card-header">
                                    <div>
                                        <strong>{selectedRound.shooterName || 'Unassigned shooter'}</strong>
                                        <p>Enter each shot, then save the round.</p>
                                    </div>
                                    <div className="rifle-target-card-summary">
                                        <span>Total {computedTotals.totalScore}</span>
                                        <span>V-bulls {computedTotals.vBullCount}</span>
                                    </div>
                                </div>

                                <div className="rifle-shot-grid">
                                    {editableScores.map((score, index) => (
                                        <div key={`${selectedRound.roundId}-${score.shotNumber}`} className="rifle-shot-card">
                                            <label htmlFor={`shot-${score.shotNumber}`}>
                                                Shot {score.shotNumber}
                                            </label>
                                            <input
                                                id={`shot-${score.shotNumber}`}
                                                type="number"
                                                min="0"
                                                max="10"
                                                step="1"
                                                value={score.value}
                                                onChange={(event) => handleScoreChange(index, event.target.value)}
                                                disabled={!canManage}
                                            />
                                            <button
                                                type="button"
                                                className={`rifle-vbull-btn ${score.vBull ? 'active' : ''}`}
                                                onClick={() => handleVBullToggle(index)}
                                                disabled={!canManage}
                                            >
                                                V
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="rifle-target-card-actions">
                                    {canManage ? (
                                        <button
                                            type="button"
                                            className="rifle-view-btn"
                                            onClick={handleSaveRound}
                                            disabled={isSavingRound}
                                        >
                                            <Check size={16} />
                                            {isSavingRound ? 'Saving...' : 'Save round'}
                                        </button>
                                    ) : null}
                                    {saveMessage ? <span className="rifle-save-message">{saveMessage}</span> : null}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="rifle-detail-empty">No rounds recorded yet.</p>
                )}
            </section>
        </div>
    );
};

export default RifleScoreSheetDetail;
